import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { Box, Card, Text } from '@factorialco/deprecated-design-system'
import { F0Select, F0Text } from '@factorialco/f0-react'
import { EntityId, EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { Textarea } from '@factorialco/f0-react/dist/experimental'
import { AvatarNameSelectorDocument } from 'generated/resources/graphql'

import { groupByTeams } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/groups/byTeams'
import {
  mapEmployees,
  subMapToEmployeeFragment,
} from 'modules/core/components/EntitySelectWrapper/Entities/Employees/lib/mappers'
import { flatSelection } from 'modules/core/components/EntitySelectWrapper/lib/flat'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import { ASSET_TYPES, AssetType, getAssetTypeLabel } from 'modules/it_management/lib/constants'

interface RequestDevicesBlockProps {
  data: WizardData
  onDataChanged: (updated: WizardData) => void
}

const MAX_NOTES_LENGTH = 280

export const RequestDevicesBlock: React.FC<RequestDevicesBlockProps> = ({
  data,
  onDataChanged,
}) => {
  const ASSET_TYPES_SET = useMemo(() => new Set<string>(ASSET_TYPES), [])

  const isValidAssetType = useCallback(
    (value: string): value is AssetType => {
      return ASSET_TYPES_SET.has(value)
    },
    [ASSET_TYPES_SET]
  )

  const { company } = useCurrentSession()
  const [selectedGroup, setSelectedGroup] = useState<string>('teams')
  const [expandedElements, setExpandedElements] = useState<EntityId[]>([])
  const [skip, setSkip] = useState<boolean>(true)

  const selectedDeviceTypes = useMemo(() => {
    const ids = data.requestedDeviceAssets || []
    return ids.filter(isValidAssetType)
  }, [data.requestedDeviceAssets])

  const handleDeviceTypesChange = useCallback(
    (value: string | string[] | undefined) => {
      const values = Array.isArray(value) ? value : value ? [value] : []
      const deviceTypes = values.filter(
        (v): v is AssetType => typeof v === 'string' && isValidAssetType(v)
      )
      onDataChanged({
        ...data,
        requestedDeviceAssets: deviceTypes,
      })
    },
    [data, onDataChanged]
  )

  const selectedPersonIds = useMemo(() => {
    if (
      !data.requestedDevicesResponsibleEmployeeIds ||
      data.requestedDevicesResponsibleEmployeeIds.length === 0
    )
      return []
    return data.requestedDevicesResponsibleEmployeeIds
  }, [data.requestedDevicesResponsibleEmployeeIds])

  const { data: employeeData, loading: employeesLoading } = useQuery(AvatarNameSelectorDocument, {
    variables: {
      companyId: company.id,
      ids: [],
      includeTeams: true,
    },
    skip: skip,
  })

  const onOpen = () => {
    if (skip) setSkip(false)
  }

  const employeeEntities = useMemo(
    () => mapEmployees(employeeData?.employees.employeeNames, []),
    [employeeData?.employees.employeeNames]
  )

  const teamsEntities = useMemo(
    () => groupByTeams(employeeData?.employees.employeeNames, [], expandedElements),
    [employeeData?.employees.employeeNames, expandedElements]
  )

  const groupMap: Record<string, EntitySelectEntity[]> = useMemo(
    () => ({
      all: employeeEntities,
      teams: teamsEntities,
    }),
    [employeeEntities, teamsEntities]
  )

  const selectedGroupedEntities = useMemo(() => {
    if (selectedPersonIds.length === 0) return []

    const selectedIds = new Set(selectedPersonIds)
    const currentEntities = groupMap[selectedGroup] || []

    if (selectedGroup === 'all') {
      return currentEntities.filter((entity) => selectedIds.has(Number(entity.id)))
    } else {
      return currentEntities
        .map((team): EntitySelectEntity | null => {
          const hasSelectedSubItems = team.subItems?.some((subItem) =>
            selectedIds.has(Number(subItem.subId))
          )
          if (!hasSelectedSubItems) return null

          const filteredSubItems = team.subItems?.filter((subItem) =>
            selectedIds.has(Number(subItem.subId))
          )
          return {
            ...team,
            subItems: filteredSubItems,
          }
        })
        .filter((team): team is EntitySelectEntity => team !== null)
    }
  }, [selectedGroup, selectedPersonIds, groupMap])

  const handlePersonChange = useCallback(
    (selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
      if (selection === null) {
        onDataChanged({
          ...data,
          requestedDevicesResponsibleEmployeeIds: undefined,
        })
        return
      }

      const entities = Array.isArray(selection) ? selection : [selection]
      let newEmployeeIds: number[]

      if (selectedGroup === 'all') {
        newEmployeeIds = entities.map((e) => Number(e.id))
      } else {
        newEmployeeIds = flatSelection(entities)
          .map((e) => subMapToEmployeeFragment(e))
          .filter((e): e is NonNullable<ReturnType<typeof subMapToEmployeeFragment>> => e !== null)
          .map((e) => e.id)
      }

      onDataChanged({
        ...data,
        requestedDevicesResponsibleEmployeeIds:
          newEmployeeIds.length > 0 ? newEmployeeIds : undefined,
      })
    },
    [data, onDataChanged, selectedGroup]
  )

  const handleNotesChange = (value: string) => {
    if (value.length <= MAX_NOTES_LENGTH) {
      onDataChanged({
        ...data,
        requestedDevicesNotes: value,
      })
    }
  }

  return (
    <Box flexDirection="column" gap="s16" width="full">
      <Card>
        <Card.Content>
          <Box flexDirection="column" gap="s16" width="full">
            <Box flexDirection="column" gap="s8">
              <Text.Header2>
                {i18n.t('add_employee_modal.devices_step.request_devices.title')}
              </Text.Header2>
              <Text size="200" color="textSecondary">
                {i18n.t('add_employee_modal.devices_step.request_devices.description')}
              </Text>
            </Box>

            <Box flexDirection="column" gap="s16" width="full">
              <Box flexDirection="column" gap="s4" width="full">
                <F0Text
                  variant="label"
                  content={i18n.t('add_employee_modal.devices_step.request_devices.devices_label')}
                />
                <F0Select
                  label=""
                  placeholder={i18n.t(
                    'add_employee_modal.devices_step.request_devices.devices_placeholder'
                  )}
                  value={selectedDeviceTypes.length > 0 ? selectedDeviceTypes : undefined}
                  onChange={handleDeviceTypesChange}
                  multiple
                  options={ASSET_TYPES.map((type) => ({
                    value: type,
                    label: getAssetTypeLabel(type),
                  }))}
                />
              </Box>

              <Box flexDirection="column" gap="s4" width="full">
                <F0Text
                  variant="label"
                  content={i18n.t(
                    'add_employee_modal.devices_step.request_devices.responsible_team_label'
                  )}
                />
                <EntitySelect
                  label=""
                  hideLabel
                  loading={employeesLoading}
                  selectedGroup={selectedGroup}
                  selectedItemsCopy="employees selected"
                  selectedLabel="selected"
                  notFoundTitle={i18n.t('employee_selector.not_found.title')}
                  notFoundSubtitle={i18n.t('employee_selector.not_found.subtitle')}
                  width={500}
                  entities={groupMap[selectedGroup] || []}
                  selectedEntities={selectedGroupedEntities}
                  groups={[
                    { label: i18n.t('employee_selector.groups.none'), value: 'all' },
                    { label: i18n.t('employee_selector.groups.teams'), value: 'teams' },
                  ]}
                  onGroupChange={(group) => setSelectedGroup(group || 'all')}
                  onSelect={handlePersonChange}
                  onItemExpandedChange={(id, expanded) => {
                    if (expanded) {
                      setExpandedElements([...expandedElements, id])
                    } else {
                      setExpandedElements(expandedElements.filter((el) => el !== id))
                    }
                  }}
                  onOpenChange={(open) => {
                    if (open) {
                      onOpen()
                    }
                  }}
                  singleSelector={false}
                  placeholder={i18n.t(
                    'add_employee_modal.devices_step.request_devices.responsible_team_placeholder'
                  )}
                />
              </Box>

              <Box flexDirection="column" gap="s4" width="full">
                <Box
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  alignItems="center"
                  width="full"
                >
                  <F0Text
                    variant="label"
                    content={i18n.t('add_employee_modal.devices_step.request_devices.notes_label')}
                  />
                  <F0Text
                    variant="label"
                    content={`${data.requestedDevicesNotes?.length || 0}/${MAX_NOTES_LENGTH}`}
                  />
                </Box>
                <Textarea
                  label=""
                  hideLabel
                  value={data.requestedDevicesNotes || ''}
                  onChange={(value: string) => handleNotesChange(value)}
                  rows={4}
                />
              </Box>
            </Box>
          </Box>
        </Card.Content>
      </Card>
    </Box>
  )
}
